import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, Sprout, Heart, Leaf, GraduationCap, PawPrint, Scale, Stethoscope, Palette, Building2, Clock, Store, HandHelping } from "lucide-react";

const CAUSE_ICONS = [
  { icon: Leaf, name: "Environment & Climate", color: "#4A6B53" },
  { icon: Heart, name: "Humanitarian Relief", color: "#D96C4A" },
  { icon: Scale, name: "Social Justice", color: "#7C4A6B" },
  { icon: PawPrint, name: "Animal Welfare", color: "#B37A2C" },
  { icon: GraduationCap, name: "Education & Mentorship", color: "#2C5F7C" },
  { icon: Stethoscope, name: "Health & Wellness", color: "#5E8A5F" },
  { icon: Palette, name: "Arts & Culture", color: "#A8533F" },
  { icon: Building2, name: "Community Development", color: "#4D5E7A" },
];

export default function Landing() {
  const { user } = useAuth();
  const isAuthed = user && typeof user === "object";
  const firstName = isAuthed ? (user.name?.split(" ")[0] || user.username) : null;

  return (
    <Layout>
    <div data-testid="landing-page" className="bg-app">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-20">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-7 fade-up">
            <span className="overline text-[var(--cm-primary)]" data-testid="hero-overline">
              {isAuthed ? `Welcome, ${firstName} — how Bahoi works` : "Bahoi — a supportive community, time-banked"}
            </span>
            <h1
              data-testid="hero-headline"
              className="font-display font-bold mt-3 leading-[1.02] text-4xl sm:text-5xl lg:text-6xl"
            >
              Find your cause.
              <br />
              <span className="italic font-light">Trade your hours</span>
              <br />
              for what you need.
            </h1>
            <p
              data-testid="hero-sub"
              className="text-ink-soft mt-6 text-base md:text-lg max-w-xl leading-relaxed"
            >
              Take a short personality assessment, get matched with causes you actually
              care about, and earn <span className="font-semibold text-ink">Time Hours</span>{" "}
              for every volunteer shift you complete. Spend those hours on services and goods
              offered by other neighbors in the community.
            </p>
            <p
              data-testid="hero-tagline"
              className="mt-4 text-sm md:text-base max-w-xl italic text-[var(--cm-primary)]"
            >
              Inspired by those ready to make change, but unsure where to start.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              {isAuthed ? (
                <>
                  <Link to="/dashboard" data-testid="cta-back-to-dashboard" className="btn-primary inline-flex items-center gap-2">
                    Back to my dashboard
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link to="/about" data-testid="cta-read-story" className="btn-outline">
                    Read the Bahoi story
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/register" data-testid="cta-get-started" className="btn-primary inline-flex items-center gap-2">
                    Take the assessment
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link to="/login" data-testid="cta-sign-in" className="btn-outline">
                    I already have an account
                  </Link>
                  <div className="time-badge ml-2">
                    <Clock className="h-3.5 w-3.5" />
                    +2 hours when you sign up
                  </div>
                </>
              )}
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              <Stat n="18" label="quiz questions" />
              <Stat n="8" label="cause areas" />
              <Stat n="∞" label="ways to give" />
            </div>
          </div>

          {/* Bento image collage */}
          <div className="md:col-span-5 md:pl-4 fade-up">
            <div className="grid grid-cols-6 grid-rows-6 gap-3 h-[460px]">
              <div className="col-span-4 row-span-4 rounded-2xl overflow-hidden border border-ink-soft">
                <img
                  alt="Volunteers planting trees together"
                  className="w-full h-full object-cover"
                  src="https://images.unsplash.com/photo-1560220604-1985ebfe28b1?auto=format&fit=crop&w=900&q=80"
                />
              </div>
              <div className="col-span-2 row-span-3 rounded-2xl bg-[var(--cm-secondary)] text-white p-4 flex flex-col justify-between">
                <span className="overline opacity-80">Time Hour</span>
                <div>
                  <div className="font-display text-4xl font-bold leading-none">1:1</div>
                  <p className="text-xs opacity-80 mt-1">
                    One hour given equals one hour earned. Every cause, every skill.
                  </p>
                </div>
              </div>
              <div className="col-span-2 row-span-3 rounded-2xl overflow-hidden border border-ink-soft">
                <img
                  alt="Hands cupping a small plant"
                  className="w-full h-full object-cover"
                  src="https://images.unsplash.com/photo-1542810634-71277d95dcbb?auto=format&fit=crop&w=600&q=80"
                />
              </div>
              <div className="col-span-4 row-span-2 rounded-2xl bg-[var(--cm-accent)] p-4 flex items-center gap-3">
                <span className="h-10 w-10 rounded-full bg-white/40 grid place-items-center text-[var(--cm-ink)]">
                  <Sprout className="h-5 w-5" />
                </span>
                <p className="font-display font-semibold text-[var(--cm-ink)] leading-tight">
                  Grown by neighbors,<br />for neighbors.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Slim invitation strip for non-volunteers */}
      <section data-testid="not-a-volunteer-strip" className="max-w-7xl mx-auto px-5 md:px-8 -mt-6 md:-mt-4">
        <div className="rounded-2xl bg-[var(--cm-accent)]/40 border border-[var(--cm-accent)] px-6 md:px-8 py-5 md:py-4 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm md:text-base leading-relaxed text-[var(--cm-ink)] max-w-3xl">
            <span className="font-display font-semibold">Not into volunteering?</span>{" "}
            <span className="text-ink-soft">
              You&apos;re still welcome to sell what you have to offer, or post opportunities
              for others to help you out!
            </span>
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <Link
              to={isAuthed ? "/marketplace" : "/register"}
              data-testid="not-vol-strip-cta"
              className="btn-primary text-sm inline-flex items-center gap-1.5 whitespace-nowrap"
            >
              {isAuthed ? "Visit marketplace" : "Sign up to join"}
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <a
              href="#all-welcome-section"
              data-testid="not-vol-strip-learn"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('[data-testid="all-welcome-section"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="text-sm font-semibold text-ink-soft hover:text-ink whitespace-nowrap hidden md:inline"
            >
              How &rarr;
            </a>
          </div>
        </div>
      </section>


      {/* How it works */}
      <section className="bg-surface border-y border-ink-soft" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20">
          <span className="overline text-[var(--cm-primary)]">How it works</span>
          <h2 className="font-display font-bold text-3xl md:text-4xl mt-2 max-w-2xl tracking-tight">
            Four steps. A whole new economy of generosity.
          </h2>
          <div className="grid md:grid-cols-4 gap-6 md:gap-8 mt-12">
            <Step n="01" title="Take the assessment" body="Answer 18 short questions about how you see the world. We map your answers to 8 cause areas." />
            <Step n="02" title="Get matched" body="See the cause you align with most — and the runners-up. You can browse all causes anytime." />
            <Step n="03" title="Volunteer" body="Sign up for online or local opportunities. Show up. Mark complete. Your organizer verifies your hours." />
            <Step n="04" title="Spend your hours" body="Use earned Time Hours at the marketplace — tutoring, cooking, ceramics, tech help, art… whatever your neighbors offer." />
          </div>
        </div>
      </section>

      {/* Cause grid */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <span className="overline text-[var(--cm-primary)]">Eight cause areas</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl mt-2 tracking-tight">
              Find the one that pulls at you.
            </h2>
          </div>
          <Link
            to={isAuthed ? "/opportunities" : "/register"}
            data-testid="causes-cta"
            className="btn-secondary text-sm"
          >
            {isAuthed ? "Browse opportunities →" : "Take the assessment →"}
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {CAUSE_ICONS.map((c) => (
            <div
              key={c.name}
              data-testid={`cause-tile-${c.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className="card-soft p-5 hover:-translate-y-0.5 transition-transform"
            >
              <span
                className="h-10 w-10 rounded-full grid place-items-center text-white"
                style={{ background: c.color }}
              >
                <c.icon className="h-5 w-5" />
              </span>
              <h3 className="font-display font-semibold mt-4 leading-tight">{c.name}</h3>
            </div>
          ))}
        </div>
      </section>

      {/* Not-into-volunteering welcome */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20" data-testid="all-welcome-section">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <span className="overline text-[var(--cm-primary)]">Everyone&apos;s welcome</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl mt-2 tracking-tight leading-tight">
              Not into volunteering? <br />There&apos;s still a place for you here.
            </h2>
            <p className="text-ink-soft mt-5 leading-relaxed">
              Bahoi isn&apos;t just for people looking to volunteer. If matching with a cause
              isn&apos;t your thing right now, you can still join the community — by offering
              what <em>you</em> already do well, or by asking for a hand when you need one.
            </p>
            <p className="text-ink-soft mt-4 leading-relaxed">
              Time Hours are the currency either way. Skip the assessment, keep the community.
            </p>
          </div>

          <div className="md:col-span-7 grid gap-5">
            <div className="card-soft p-7 border-l-4 border-[var(--cm-secondary)]" data-testid="welcome-card-market">
              <div className="flex items-start gap-4">
                <span className="h-11 w-11 rounded-full bg-[var(--cm-secondary)]/15 grid place-items-center flex-shrink-0">
                  <Store className="h-5 w-5 text-[var(--cm-secondary)]" />
                </span>
                <div>
                  <h3 className="font-display font-semibold text-xl leading-tight">
                    Sell what you love making
                  </h3>
                  <p className="text-ink-soft mt-2 leading-relaxed text-sm">
                    A great baker, a patient tutor, a green thumb, a fixer of broken things?
                    List your goods or services in the marketplace and earn Time Hours whenever
                    a neighbor redeems what you offer. Then spend those hours on things
                    <em> you</em> want — no volunteer shift required.
                  </p>
                  <Link
                    to={isAuthed ? "/marketplace/new" : "/register"}
                    data-testid="welcome-market-cta"
                    className="text-sm font-semibold text-[var(--cm-secondary)] inline-flex items-center gap-1.5 mt-4 hover:underline underline-offset-4"
                  >
                    {isAuthed ? "Offer something →" : "Sign up and offer something →"}
                  </Link>
                </div>
              </div>
            </div>

            <div className="card-soft p-7 border-l-4 border-[var(--cm-primary)]" data-testid="welcome-card-host">
              <div className="flex items-start gap-4">
                <span className="h-11 w-11 rounded-full bg-[var(--cm-primary)]/15 grid place-items-center flex-shrink-0">
                  <HandHelping className="h-5 w-5 text-[var(--cm-primary)]" />
                </span>
                <div>
                  <h3 className="font-display font-semibold text-xl leading-tight">
                    Ask for a hand
                  </h3>
                  <p className="text-ink-soft mt-2 leading-relaxed text-sm">
                    Working on a project, running an event, or just need help lugging boxes on
                    a weekend? Post an opportunity and let neighbors show up in exchange for
                    Time Hours. Community, on your terms.
                  </p>
                  <Link
                    to={isAuthed ? "/opportunities/new" : "/register"}
                    data-testid="welcome-host-cta"
                    className="text-sm font-semibold text-[var(--cm-primary)] inline-flex items-center gap-1.5 mt-4 hover:underline underline-offset-4"
                  >
                    {isAuthed ? "Post an opportunity →" : "Sign up and post one →"}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-24">
        <div
          data-testid="final-cta"
          className="rounded-3xl bg-[var(--cm-ink)] text-[var(--cm-surface)] px-8 md:px-16 py-16 md:py-20 grid md:grid-cols-2 gap-10 items-center"
        >
          <div>
            <span className="overline text-[var(--cm-accent)]">
              {isAuthed ? "Keep going" : "Start your match"}
            </span>
            <h2 className="font-display font-bold text-3xl md:text-5xl mt-3 leading-tight tracking-tight">
              {isAuthed ? "Your next hour is waiting." : "The first hour is on us."}
            </h2>
            <p className="text-white/70 mt-4 max-w-md">
              {isAuthed
                ? "Find a shift that fits your week, mark it complete, and watch your balance grow. Or browse the marketplace and spend what you've earned."
                : "Create an account and you'll get 2 Time Hours immediately to spend in the marketplace — even before your first shift."}
            </p>
          </div>
          <div className="md:justify-self-end flex flex-wrap gap-3">
            {isAuthed ? (
              <>
                <Link to="/opportunities" data-testid="final-cta-opportunities" className="btn-primary text-lg px-8 py-4 inline-flex items-center gap-2">
                  Find a shift
                  <ArrowRight className="h-5 w-5" />
                </Link>
                <Link to="/marketplace" data-testid="final-cta-marketplace" className="btn-outline border-white text-white hover:bg-white hover:text-[var(--cm-ink)] text-lg px-6 py-4">
                  Marketplace
                </Link>
              </>
            ) : (
              <Link to="/register" data-testid="final-cta-button" className="btn-primary text-lg px-8 py-4 inline-flex items-center gap-2">
                Get matched
                <ArrowRight className="h-5 w-5" />
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
    </Layout>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="font-display text-3xl font-bold">{n}</div>
      <div className="text-xs text-ink-soft uppercase tracking-widest mt-1">{label}</div>
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <div className="card-soft p-6">
      <span className="overline text-[var(--cm-primary)]">{n}</span>
      <h3 className="font-display font-semibold text-lg mt-2">{title}</h3>
      <p className="text-sm text-ink-soft mt-3 leading-relaxed">{body}</p>
    </div>
  );
}
